import type { AuthProvider } from "ra-core";
import type { Sale, UserRole } from "./dataGenerator/types";

export const DEFAULT_USER: Sale = {
  id: 1,
  first_name: "Jane",
  last_name: "Doe",
  email: "jane.doe@finturf.com",
  role: "admin",
  avatar: { src: "https://i.pravatar.cc/150?u=jane" },
};

export const USER_STORAGE_KEY = "user";

// Initialize with default user
localStorage.setItem(USER_STORAGE_KEY, JSON.stringify({ ...DEFAULT_USER }));

export const getCurrentUser = (): Sale | null => {
  const stored = localStorage.getItem(USER_STORAGE_KEY);
  if (!stored) return null;
  return JSON.parse(stored) as Sale;
};

export const setCurrentUser = (user: Sale) => {
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
};

export const authProvider: AuthProvider = {
  login: async ({ username }: { username?: string } = {}) => {
    // For demo: allow switching users by email
    // In production, this would validate credentials
    const user = username ? { ...DEFAULT_USER, email: username } : DEFAULT_USER;
    setCurrentUser(user);
    return Promise.resolve();
  },
  logout: () => {
    localStorage.removeItem(USER_STORAGE_KEY);
    return Promise.resolve();
  },
  checkError: () => Promise.resolve(),
  checkAuth: () =>
    localStorage.getItem(USER_STORAGE_KEY)
      ? Promise.resolve()
      : Promise.reject(),
  getIdentity: () => {
    const user = getCurrentUser();
    if (!user) return Promise.reject();
    return Promise.resolve({
      // Pass through the full user object for role checking
      ...user,
      // Override with identity-specific fields (must come after spread)
      id: user.id,
      fullName: `${user.first_name} ${user.last_name}`,
      avatar: user.avatar?.src,
    });
  },
  canAccess: async ({
    resource,
    action,
  }: {
    resource?: string;
    action?: string;
  }) => {
    const user = getCurrentUser();
    if (!user) return false;

    const role: UserRole = user.role || "sales_rep";

    // Admin can do everything
    if (role === "admin") return true;

    // Manager can manage most things but not settings (write)
    if (role === "manager") {
      if (resource === "settings" && action !== "list" && action !== "show") {
        return false;
      }
      return true;
    }

    // Sales rep has limited access
    if (role === "sales_rep") {
      // Can't access settings at all
      if (resource === "settings") return false;
      // Can't manage users
      if (resource === "sales" && action !== "list" && action !== "show") {
        return false;
      }
      return true;
    }

    return false;
  },
};
